import { Buffer } from "buffer";
import React from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { router } from "./router";
import { ThemeProvider } from "./components/ThemeProvider";
import { configureAmplify } from "./config/amplify";
import "./i18n";
import "./index.css";

// KAN-146: @react-pdf/renderer reaches for Node's `Buffer` inside its
// browser bundle (fontkit, image pipelines). Vite doesn't polyfill it
// automatically, so we attach it here once at boot — without it, badge
// generation crashes with "Buffer is not defined".
if (typeof window !== "undefined" && !(window as { Buffer?: unknown }).Buffer) {
  (window as unknown as { Buffer: typeof Buffer }).Buffer = Buffer;
}

configureAmplify();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // Hold cached query data for 10 min after observers unmount, so quick
      // tab switches and navigations rehydrate from cache instead of refetching.
      gcTime: 10 * 60_000,
      refetchOnWindowFocus: false
    },
    mutations: {
      // One retry on transient network failures; user errors (4xx) are not
      // retried by react-query unless retry is set explicitly.
      retry: 1
    }
  }
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <RouterProvider router={router} />
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </ThemeProvider>
    </QueryClientProvider>
  </React.StrictMode>
);
